import { Modal } from "@material-ui/core";
import React, { useState } from "react";
import styled from "styled-components";
import theme from "../../../../assets/theme";
import { CloseBtn } from "./CaseTeam";
import { COLORS } from "../HearingBundles/tokens";

const getSideColor = (side) => {
  return side === "claimant"
    ? { dot: "#F59E0B", bg: "#FEF3E2", fg: "#B45309", border: "#FCD34D" }
    : { dot: "#3B82F6", bg: "#EFF6FF", fg: "#1E40AF", border: "#93C5FD" };
};

const AgentDetailsModal = ({ onClose, partyData }) => {
  const [selectedAgent, setSelectedAgent] = useState(null);

  if (!partyData) return null;

  const partyRole = partyData.partyRole;
  let rawParty = partyData.party || {};
  if (rawParty?.party) rawParty = rawParty.party;

  const agents = rawParty?.agents || [];
  const partyName = rawParty?.name || "Party Name";
  const sideColor = getSideColor(partyRole);

  const handleAgentSelect = (agent) => setSelectedAgent(agent);

  return (
    <Modal open={true} onClose={onClose} disableRestoreFocus>
      <ModalOverlay>
        <ModalWrapper>
          <ModalHeader>
            <ModalAccent color={sideColor.dot} />
            <HeaderContent>
              <ModalTitle>Agent Details</ModalTitle>
              <ModalSubtitle>
                {partyRole === "claimant" ? "Claimant" : "Respondent"} •{" "}
                {partyName}
              </ModalSubtitle>
            </HeaderContent>
            <CloseWrapper>
              <CloseBtn onClick={onClose}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </CloseBtn>
            </CloseWrapper>
          </ModalHeader>

          <ModalBody>
            {agents.length === 0 ? (
              <EmptyState>
                <EmptyTitle>No Agents Added</EmptyTitle>
                <EmptySubtitle>
                  This party hasn't added any agents yet.
                </EmptySubtitle>
              </EmptyState>
            ) : selectedAgent ? (
              <></>
            ) : (
              <AgentTableView
                agents={agents}
                sideColor={sideColor}
                onAgentClick={handleAgentSelect}
              />
            )}
          </ModalBody>
        </ModalWrapper>
      </ModalOverlay>
    </Modal>
  );
};

export default AgentDetailsModal;

const AgentTableView = ({ agents, sideColor, onAgentClick }) => (
  <TableContainer>
    <Table>
      <thead>
        <tr>
          <Th>Agent</Th>
          <Th>Email</Th>
          <Th>Mobile</Th>
          <Th>Role</Th>
        </tr>
      </thead>
      <tbody>
        {agents.map((agent) => (
          <TableRow key={agent.id}>
            <Td>
              <AgentCell>
                <div>
                  <AgentName>
                    {agent.user?.name ? agent?.user?.name : "—"}
                  </AgentName>
                </div>
              </AgentCell>
            </Td>
            <Td>{agent.email}</Td>
            <Td>{agent.user?.mobile || "—"}</Td>
            <Td>
              <RolePill $primary={agent.role === "primary"}>
                {agent.role === "primary" ? "Primary" : "Secondary"}
              </RolePill>
            </Td>
          </TableRow>
        ))}
      </tbody>
    </Table>
  </TableContainer>
);

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1200;
`;

const ModalWrapper = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.35);
  width: 100%;
  max-width: 780px;
  max-height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 24px 28px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ModalAccent = styled.div`
  width: 6px;
  height: 42px;
  background: ${(props) => props.color};
  border-radius: 9999px;
`;

const HeaderContent = styled.div``;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #1e2937;
  margin: 0;
`;

const ModalSubtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 4px 0 0;
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 28px;
  background: #f8fafc;
`;

/* Table Styles */
const TableContainer = styled.div`
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  background: white;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 14px 20px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableRow = styled.tr`
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
  }

  td {
    padding: 16px 20px;
    border-bottom: 1px solid #f1f5f9;
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const Td = styled.td`
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontRegular};
`;

const AgentCell = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const AgentName = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.COLOR_DARK};
`;

const RolePill = styled.span`
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 600;
  background: ${(props) => (props.$primary ? "#ecfdf5" : "#f1f5f9")};
  color: ${(props) => (props.$primary ? "#10b981" : "#64748b")};
`;

/* Empty State */
const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #64748b;
`;

const EmptyTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #1e2937;
  margin-bottom: 8px;
`;

const EmptySubtitle = styled.div`
  font-size: 14px;
`;

export const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
`;

const CloseWrapper = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding-left: 12px;
`;
